import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PicksCtx = createContext(null);
export const usePicks = () => useContext(PicksCtx) || {};

export function PicksProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const isInfluencer = user?.influencer_status === "approved" || user?.role === "admin";

  const refresh = useCallback(() => {
    if (!isInfluencer) { setIds(new Set()); return; }
    api.get("/my/pick-ids").then(({ data }) => setIds(new Set(data.ids))).catch(() => {});
  }, [isInfluencer]);

  useEffect(() => { refresh(); }, [refresh]);

  // One-time tip so new influencers discover the click-to-pick flow.
  useEffect(() => {
    if (!isInfluencer) return;
    if (localStorage.getItem("btc_picks_tip_seen")) return;
    const t = setTimeout(() => {
      toast("⭐ Tip: tap \"Add to my picks\" on any product to feature it on your pick page.", { duration: 6500 });
      localStorage.setItem("btc_picks_tip_seen", "1");
    }, 1800);
    return () => clearTimeout(t);
  }, [isInfluencer]);

  const isPicked = (pid) => ids.has(pid);

  const addPick = async (pid, note = "") => {
    try {
      await api.post("/picks", { product_id: pid, note });
      setIds((s) => new Set(s).add(pid));
      toast.success("Added to your picks! ✨");
      return true;
    } catch (e) {
      toast.error(apiError(e, "Couldn't add to picks"));
      return false;
    }
  };

  return (
    <PicksCtx.Provider value={{ isInfluencer, isPicked, addPick, refresh }}>
      {children}
    </PicksCtx.Provider>
  );
}
